/**
 * Create User Feedback Hook
 *
 * Submits a rating-based feedback form via GraphQL mutation.
 */

import { useMutation } from '@tanstack/react-query';
import { axiosInstance } from '@/dataProvider/axios';
import toast from 'react-hot-toast';

const CREATE_USER_FEEDBACK_MUTATION = `
  mutation CreateUserFeedback($input: CreateUserFeedbackInput!) {
    createUserFeedback(input: $input) {
      id
      userId
      workflowRating
      agentConfigRating
      metadataExplorerRating
      overallRating
      recommendRating
      insertedAt
    }
  }
`;

export interface CreateUserFeedbackInput {
  workflowRating: number;
  agentConfigRating: number;
  metadataExplorerRating: number;
  overallRating: number;
  recommendRating: number;
}

interface UserFeedbackResponse {
  id: string;
  userId: string;
  workflowRating: number;
  agentConfigRating: number;
  metadataExplorerRating: number;
  overallRating: number;
  recommendRating: number;
  insertedAt: string;
}

interface CreateUserFeedbackData {
  createUserFeedback: UserFeedbackResponse;
}

/**
 * Hook to submit user feedback ratings.
 *
 * @example
 * const { mutate, isPending } = useCreateUserFeedback();
 *
 * mutate({
 *   workflowRating: 5,
 *   agentConfigRating: 4,
 *   metadataExplorerRating: 4,
 *   overallRating: 5,
 *   recommendRating: 5,
 * });
 */
export function useCreateUserFeedback() {
  return useMutation<UserFeedbackResponse, Error, CreateUserFeedbackInput>({
    mutationFn: async (input: CreateUserFeedbackInput) => {
      const response = await axiosInstance.post<CreateUserFeedbackData>('/graphql', {
        query: CREATE_USER_FEEDBACK_MUTATION,
        variables: { input },
      });
      return response.data.createUserFeedback;
    },
    onSuccess: () => {
      toast.success('Thank you for your feedback!');
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to submit feedback. Please try again.');
    },
  });
}
